import { GetMembershipDetails } from '../contracts';
import {
  AdditionalDiscountType,
  GenderCode,
  MSSMobileAuthStatus,
  MSSProductStatus,
  MSSSpinOffStatus,
} from '@lib/types';
import {
  CurrentPayPlan,
  GenderType,
  MSSMemberStatus,
  MembershipLevel,
  MembershipProductType,
  PhoneType,
  RoleType,
  CartProductCode,
  MSSPriceSectionId,
} from '@ace-clublabs/common-membership-api-types';
import { IMemberDetails } from '../../state/model';

export const GetMembershipDetailsStub: GetMembershipDetails = async () => {
  return await Promise.resolve(getMembershipDetailsApiStub);
};

export const getMembershipDetailsApiStub: IMemberDetails = {
  sessionId: 'some-session-id',
  clubCode: '004',
  loggedInCustomerId: '12345678',
  clubRegion: '00',
  termEffectiveDate: '2024-06-24',
  termExpirationDate: '2025-06-24',
  priorTermExpirationDate: '2024-06-24',
  membershipNumber: '6200041234567801',
  currentBillPlan: CurrentPayPlan.ONETIME,
  isAutopayRequired: false,
  joinYear: '2015',
  inRenewal: false,
  inArrears: true,
  inGrace: false,
  campaignCode: '000007',
  members: [
    {
      roleCode: '00',
      customerId: '12345678',
      firstName: 'JANET',
      middleName: 'SMITH',
      lastName: 'MORALES',
      nameSuffix: 'Sr',
      gender: GenderType.FEMALE,
      dateOfBirth: '1990-05-30',
      roleType: RoleType.PRIMARY,
      status: MSSMemberStatus.EXISTING,
      phoneNumbers: [
        {
          type: PhoneType.MOBILE,
          areaCode: '555',
          prefix: '555',
          suffix: '5555',
          mobileAuthStatus: MSSMobileAuthStatus.CONSENTED,
        },
        {
          type: PhoneType.HOME,
          areaCode: '666',
          prefix: '666',
          suffix: '6666',
          mobileAuthStatus: MSSMobileAuthStatus.NOT_RESPONDED,
          isMobilePhone: true,
        },
      ],
      emailAddress: 'jmorales@email.com',
      rules: {
        viewMembership: true,
        changeMembership: true,
        makePayment: true,
        updateEmail: true,
        updatePhone: true,
        nameChange: {
          firstName: true,
          middleName: true,
          lastName: true,
          suffix: true,
        },
      },
      residenceAddress: {
        addressLine1: '123 MAIN ST',
        addressLine2: 'APARTMENT 1',
        city: 'LOS ANGELES',
        state: 'CA',
        zip: '90007',
      },
      mailingAddress: {
        addressLine1: '456 MAIN ST',
        city: 'LOS ANGELES',
        state: 'CA',
        zip: '90007',
      },
      joinYear: '2015',
    },
    {
      roleCode: '06',
      customerId: '11111111',
      firstName: 'JAMES',
      lastName: 'MORALES',
      roleType: RoleType.ADULT_ASSOCIATE,
      status: MSSMemberStatus.EXISTING,
      gender: GenderType.MALE,
      dateOfBirth: null,
      emailAddress: 'associate@aaa.com',
      rules: {
        viewMembership: true,
        changeMembership: true,
        makePayment: true,
        updateEmail: true,
        updatePhone: true,
        nameChange: {
          firstName: true,
          middleName: true,
          lastName: true,
          suffix: true,
        },
      },
      phoneNumbers: [],
      residenceAddress: {
        addressLine1: '123 MAIN ST',
        addressLine2: 'APARTMENT 1',
        city: 'LOS ANGELES',
        state: 'CA',
        zip: '90007',
      },
      mailingAddress: {
        addressLine1: '456 MAIN ST',
        city: 'LOS ANGELES',
        state: 'CA',
        zip: '90007',
      },
    },
    {
      roleCode: '01',
      customerId: '22222222',
      firstName: 'JOE',
      lastName: 'MORALES',
      middleName: 'AUSTIN',
      nameSuffix: 'Jr.', //confirm what format suffix is returned from api
      roleType: RoleType.DEPENDENT,
      status: MSSMemberStatus.EXISTING,
      dateOfBirth: '2005-08-01',
      gender: GenderType.MALE,
      rules: {
        viewMembership: false,
        changeMembership: false,
        makePayment: false,
        updateEmail: true,
        updatePhone: true,
        nameChange: {
          firstName: true,
          middleName: true,
          lastName: true,
          suffix: true,
        },
      },
      residenceAddress: {
        addressLine1: '123 MAIN ST',
        addressLine2: 'APARTMENT 1',
        city: 'LOS ANGELES',
        state: 'CA',
        zip: '90007',
      },
      mailingAddress: {
        addressLine1: '456 MAIN ST',
        city: 'LOS ANGELES',
        state: 'CA',
        zip: '90007',
      },
      spinOffStatus: MSSSpinOffStatus.SPIN_OFF,
    },
  ],
  loggedInMember: {
    emailAddress: 'jmorales@email.com',
    customerId: '12345678',
    firstName: 'JANET',
    lastName: 'MORALES',
    middleName: 'SMITH',
    nameSuffix: 'Jr.',
    gender: GenderType.FEMALE,
    dateOfBirth: '1990-05-30',
    roleType: RoleType.PRIMARY,
    roleCode: '00',
    status: MSSMemberStatus.EXISTING,
    mobilePhoneNumber: '555-555-5555',
    secured: false,
    phoneNumbers: [
      {
        type: PhoneType.MOBILE,
        areaCode: '555',
        prefix: '555',
        suffix: '5555',
        mobileAuthStatus: MSSMobileAuthStatus.CONSENTED,
      },
      {
        type: PhoneType.HOME,
        areaCode: '666',
        prefix: '666',
        suffix: '6666',
        mobileAuthStatus: MSSMobileAuthStatus.NOT_RESPONDED,
        isMobilePhone: true,
      },
    ],
    residenceAddress: {
      addressType: 'RS',
      addressTypeDescription: 'Residence',
      addressLine1: '123 MAIN ST',
      addressLine2: 'APARTMENT 1',
      city: 'LOS ANGELES',
      state: 'CA',
      zip: '90007',
    },
    mailingAddress: {
      addressType: 'MB',
      addressTypeDescription: 'Mailing',
      addressLine1: '456 MAIN ST',
      city: 'LOS ANGELES',
      state: 'CA',
      zip: '90007',
    },
    rules: {
      viewMembership: true,
      changeMembership: true,
      makePayment: true,
      updateEmail: true,
      updatePhone: true,
      nameChange: {
        firstName: true,
        middleName: true,
        lastName: true,
        suffix: true,
      },
    },
  },
  benefitLevels: {
    existingLevel: MembershipLevel.PLUS,
    pendingLevel: MembershipLevel.PREMIER,
    effectiveDate: '6/24/2024',
  },
  products: [
    {
      type: MembershipProductType.RV,
      status: MSSProductStatus.EXISTING,
      effectiveDate: '06-30-2024',
    },
  ],
  enrollments: [],
  hasPaymentInTransit: false,
  paymentInTransitDetails: [
    //uncomment when hasPaymentInTransit is true
    // {
    //   pitDate: '2024-03-21',
    //   pitAmount: '158',
    // },
  ],
  pendingProducts: [
    // {
    //   productType: MembershipLevel.PREMIER,
    //   effectiveDate: '6/24/2024',
    //   waitingPeriod: '',
    // },
    // {
    //   productType: MembershipProductType.RV,
    //   effectiveDate: '6/24/2024',
    //   waitingPeriod: '',
    // },
  ],
  memberCosts: {
    priceCart: {
      total: 102,
      savings: 0,
      salesTax: 6.12,
      items: [
        {
          id: MSSPriceSectionId.SERVICE_LEVEL_COST,
          code: CartProductCode.SERVICELEVEL,
          description: 'Janet - primary',
          price: 57,
        },
        {
          id: 'some-virtual-id',
          code: CartProductCode.ADULT,
          description: `James - adult`,
          price: 27,
        },
        // {
        //   id: 'some-virtual-id',
        //   code: CartProductCode.ASSOCIATE,
        //   description: `Jim - associate`,
        //   price: 27,
        // },
        // {
        //   id: 'some-virtual-id',
        //   code: CartProductCode.DEPENDENT,
        //   description: `Joe - dependent`,
        //   price: 27,
        // },
        // {
        //   id: MSSPriceSectionId.FAMILY_COST,
        //   code: CartProductCode.FAMILYCOST,
        //   description: 'Family upgrade fee',
        //   price: 15,
        // },
        {
          id: 'RV_COST',
          code: CartProductCode.RV,
          description: 'Roadside for RV/motorcycle',
          price: 42,
        },
        {
          id: 'SERVICE_LEVEL_COST-Discount',
          code: CartProductCode.SERVICELEVEL,
          description: 'Janet - primary',
          price: 42,
          discount: 24,
        },
      ],
    },
    prospectiveCosts: {
      classic: {
        memberRoles: {
          AD: 35,
          DE: 37,
        },
        products: {
          RV: 42,
        },
        discounts: {
          autopay: 4.5,
        },
      },
      plus: {
        memberRoles: {
          AD: 35,
          DE: 37,
        },
        products: {
          RV: 42,
        },
        discounts: {
          autopay: 4.5,
        },
      },
      premier: {
        memberRoles: {
          AD: 35,
          DE: 37,
        },
        products: {
          RV: 42,
        },
        discounts: {
          autopay: 4.5,
        },
      },
    },
    additionalDiscounts: [
      { type: AdditionalDiscountType.RENEWAL, value: 5 },
      // { type: AdditionalDiscountType.RETIREE, value: 57 },
      // { type: AdditionalDiscountType.EMPLOYEE, value: 57 },
      // { type: AdditionalDiscountType.TMOBILE_YOU, value: 57 },
    ],
  },
  prospectiveMemberRules: {
    allowedBillPlans: [],
    allowedGenders: [GenderCode.F, GenderCode.M, GenderCode.X],
    allowedMembershipLevels: [MembershipLevel.CLASSIC, MembershipLevel.PLUS, MembershipLevel.PREMIER],
    autopayDiscountAllowed: false,
    downgradesAllowed: true,
    memberProducts: [
      {
        productType: MembershipProductType.RV,
        productRules: [
          {
            membershipLevel: MembershipLevel.CLASSIC,
            rule: MSSProductStatus.ALLOWED,
          },
          {
            membershipLevel: MembershipLevel.PLUS,
            rule: MSSProductStatus.ALLOWED,
          },
          {
            membershipLevel: MembershipLevel.PREMIER,
            rule: MSSProductStatus.ALLOWED,
          },
        ],
      },
    ],
    memberRoles: [
      {
        roleType: RoleType.PRIMARY,
        maximumAge: 140,
        minimumAge: 0,
        maximumAllowed: 1,
        isBirthDateRequired: false,
      },
      {
        roleType: RoleType.ADULT_ASSOCIATE,
        maximumAge: 140,
        minimumAge: 0,
        maximumAllowed: 1,
        isBirthDateRequired: false,
      },
      {
        roleType: RoleType.DEPENDENT,
        maximumAge: 20,
        minimumAge: 0,
        maximumAllowed: 2,
        isBirthDateRequired: true,
      },
    ],
    upgradesAllowed: true,
  },
};
